import EditOutlined from '@material-ui/icons/EditOutlined'
import OpenInNewOutlined from '@material-ui/icons/OpenInNewOutlined'
import { Box, Button, Card, CardContent, Grid, styled, TextField, Tooltip, Typography } from 'opensolar-ui'
import { usePreviewMode } from '../previewMode'
import {
  CheckoutFinanceAction,
  ExpectedMilestonePaymentType,
  LenderType,
} from 'opensolar-ui/src/checkout/checkoutFinancing/types'
import { useState } from 'react'
import { formatCurrencyWithSymbol } from '../utils'
import LoanMilestoneTimeline from './LoanMilestoneTimeline'

const TimelineContainer = styled('div')(() => ({
  margin: '16px 0px',
}))
const TimelineTitle = styled(Typography)(() => ({
  marginBottom: '25px',
}))
const LoanAmountCardContent = styled(CardContent)(() => ({
  textAlign: 'center',
  padding: '13px 14px !important',
}))
const LoanAmountTitle = styled('div')(() => ({
  marginBottom: '10px',
  whiteSpace: 'nowrap',
  color: '#212121',
}))
const StyledEditButton = styled(EditOutlined)(() => ({
  margin: '0px !important',
}))
const EditTextField = styled(TextField)(() => ({
  height: '28px',
  padding: '6px 0px',
}))
const LenderCard = styled(Card)(() => ({
  marginBottom: '19px',
}))
const LenderCardContent = styled(CardContent)(() => ({
  display: 'flex',
}))
const LenderLogo = styled('img')(() => ({
  marginBottom: '16px',
}))
const LenderDescription = styled(Typography)(() => ({
  whiteSpace: 'pre-line', // respects linebreaks
  marginBottom: '16px',
}))
const ApplyButtonWrapper = styled(Grid)(() => ({
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
  width: '100%',
  height: '100%',
}))
const ApplyButton = styled(Button)(() => ({
  marginLeft: 'auto',
}))
const LoadingDotsWrapper = styled('div')(() => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  opacity: 0.5,
  backgroundColor: '#F6F6F6',
  zIndex: 10,
}))

const LenderReferralDialogContent = (props: {
  onApply: (lender: LenderType) => Promise<void>
  onEditLoanAmount: (field) => void
  actionData: CheckoutFinanceAction
  currencySymbol: string
  expectedMilestonePayments?: ExpectedMilestonePaymentType[] | undefined
  lenders: LenderType[]
  translate: (text: string) => string
  setIsSubmitting: (isSubmitting: boolean) => void
}) => {
  const {
    onApply,
    onEditLoanAmount,
    actionData,
    expectedMilestonePayments,
    currencySymbol,
    lenders,
    translate,
    setIsSubmitting,
  } = props

  const [loanAmount, setLoanAmount] = useState(actionData?.pricing?.system_price_including_tax)
  const [isEditingAmount, setIsEditingAmount] = useState(false)
  const [milestonesChecked, setMilestonesChecked] = useState<{ [key: number]: boolean }>({})
  const previewMode = usePreviewMode()

  const handleApplyNowButton = async (lender) => {
    setIsSubmitting(true)
    await onApply(lender)
    setIsSubmitting(false)
  }

  return (
    <>
      {!!lenders.length && (
        <TimelineContainer>
          {!!expectedMilestonePayments?.length && (
            <TimelineTitle variant="subtitle2">{translate('Select financed amount')}</TimelineTitle>
          )}
          <Grid container alignItems="center">
            {!!expectedMilestonePayments?.length && (
              <Grid item xs={12} md={9}>
                <LoanMilestoneTimeline
                  onChangeTotalAmount={(amount) => setLoanAmount(amount)}
                  milestonesChecked={milestonesChecked}
                  setMilestonesChecked={(value) => {
                    setMilestonesChecked(value)
                  }}
                  milestones={expectedMilestonePayments}
                  currencySymbol={currencySymbol}
                  onCheck={() => onEditLoanAmount('checkbox')}
                />
              </Grid>
            )}
            <Grid item xs={12} md={3}>
              <Card>
                <LoanAmountCardContent>
                  <LoanAmountTitle>{translate('Total loan amount')}</LoanAmountTitle>
                  {!isEditingAmount ? (
                    <Button onClick={() => setIsEditingAmount(true)} variant="text">
                      <Box display="flex" alignItems="center">
                        <Typography variant="h6">
                          {`${formatCurrencyWithSymbol(loanAmount, currencySymbol, undefined, 0)}`}
                        </Typography>
                        <StyledEditButton />
                      </Box>
                    </Button>
                  ) : (
                    <EditTextField
                      type="number"
                      variant="standard"
                      value={loanAmount}
                      onChange={(e) => {
                        setMilestonesChecked({})
                        setLoanAmount(Number(e.target.value))
                      }}
                      onBlur={() => {
                        onEditLoanAmount('textfield')
                        setIsEditingAmount(false)
                      }}
                      autoFocus
                    />
                  )}
                </LoanAmountCardContent>
              </Card>
            </Grid>
          </Grid>
        </TimelineContainer>
      )}
      {lenders.map((lender) => (
        <LenderCard key={lender.title}>
          <LenderCardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <LenderLogo
                  src={lender.logo_url || undefined}
                  height={lender.logo_height || '56px'}
                  alt={`${lender.title} logo`}
                />
                <LenderDescription variant="subtitle2">{lender.main_text}</LenderDescription>
                <LenderDescription variant="body2" dangerouslySetInnerHTML={{ __html: lender.sub_text || '' }} />
              </Grid>
              <ApplyButtonWrapper container item xs={12} md={4}>
                {lender.show_apply_button && (
                  previewMode ? (
                    <Tooltip
                      title={translate(
                        'Preview mode: applications are disabled. Share this link with your customer so they can apply.'
                      )}
                    >
                      <span>
                        <ApplyButton variant="contained" color="secondary" disabled endIcon={<OpenInNewOutlined />}>
                          {translate('Check Availability')}
                        </ApplyButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <ApplyButton
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        handleApplyNowButton(lender)
                      }}
                      endIcon={<OpenInNewOutlined />}
                    >
                      {translate('Check Availability')}
                    </ApplyButton>
                  )
                )}
              </ApplyButtonWrapper>
            </Grid>
          </LenderCardContent>
        </LenderCard>
      ))}
    </>
  )
}

export default LenderReferralDialogContent
